<template>
  <main class="home premium-home">
    <div class="hero-stage">
    <section class="home-hero container">
      <div class="hero-copy">
        <p class="eyebrow"><span class="status-dot"></span>{{ $t("premium.eyebrow") }}</p>
        <h1>
          {{ $t("premium.heroTitle") }}<br /><em>{{ $t("premium.heroAccent") }}</em>
        </h1>
        <p class="hero-description">{{ $t("premium.heroDescription") }}</p>
        <div class="hero-actions">
          <a href="#get-in-touch" class="btn btn-primary">
{{ $t("premium.findShifts") }} <i class="fa fa-arrow-right" aria-hidden="true"></i>
</a>
          <a href="#get-in-touch" class="btn btn-outline-primary" @click="$refs.contact.selectOrganisation()">{{ $t("premium.findStaff") }}</a>
        </div>
        <div class="hero-note">
          <span class="note-icon"><i class="fa fa-check" aria-hidden="true"></i></span>{{ $t("premium.heroNote") }}
        </div>
      </div>
      <div class="hero-visual">
        <img
          class="hero-photo"
          src="@/assets/images/nursing-team-generated.jpg"
          :alt="$t('premium.teamAlt')"
          fetchpriority="high"
          width="1122"
          height="1402"
        />
        <div class="photo-label"><span class="status-dot"></span>ALL CARE · CYPRUS</div>
        <div class="hero-caption">
          <span class="caption-icon"><i class="fa fa-heart-pulse" aria-hidden="true"></i></span>
          <div>
            <strong>{{ $t("premium.peopleFirst") }}</strong><span>{{ $t("premium.peopleFirstDetail") }}</span>
          </div>
        </div>
</div>
    </section>
    </div>
    <div class="network-strip">
      <div class="container">
        <span>{{ $t("All Cyprus, one network") }}</span><span>{{ $t("NICOSIA") }}</span><span>{{ $t("LIMASSOL") }}</span><span>{{ $t("LARNACA") }}</span><span>{{ $t("PAPHOS") }}</span><span>{{ $t("FAMAGUSTA") }}</span>
      </div>
    </div>
    <stats-component />
    <section id="about-us" class="container home-section">
      <div class="about-intro">
        <div class="about-photograph">
          <img src="@/assets/images/nursing-care-generated.jpg" :alt="$t('premium.nursingCareAlt')" width="1254" height="1254" loading="lazy" decoding="async">
        </div>
        <div class="section-heading">
        <p class="eyebrow">01 / {{ $t("About us") }}</p>
        <h2>
          {{ $t("premium.aboutTitle") }}<br /><em>{{ $t("premium.aboutAccent") }}</em>
        </h2>
        <p>{{ $t("premium.aboutDescription") }}</p>
        <p>{{ $t("premium.aboutProcess") }}</p>
        <a class="text-link" href="#get-in-touch">{{ $t("Get In Touch") }} <i class="fa fa-arrow-right" aria-hidden="true"></i></a>
        </div>
      </div>
      <div class="audience-grid">
        <article class="audience-card">
          <span class="feature-icon"><i class="fa fa-user-nurse" aria-hidden="true"></i></span>
          <p class="eyebrow">{{ $t("premium.forNurses") }}</p>
          <h3>{{ $t("premium.nurseTitle") }}</h3>
          <p>{{ $t("premium.nurseDescription") }}</p>
          <a href="#get-in-touch" class="text-link">
{{ $t("premium.findShifts") }} <i class="fa fa-arrow-right" aria-hidden="true"></i>
</a>
        </article>
        <article class="audience-card audience-card-dark">
          <span class="feature-icon"><i class="fa fa-house-medical" aria-hidden="true"></i></span>
          <p class="eyebrow">{{ $t("premium.forOrganisations") }}</p>
          <h3>{{ $t("premium.organisationTitle") }}</h3>
          <p>{{ $t("premium.organisationDescription") }}</p>
          <router-link to="/organisations" class="text-link">{{ $t("premium.forOrganisations") }} <i class="fa fa-arrow-right" aria-hidden="true"></i></router-link>
        </article>
      </div>
    </section>
    <how-it-works />
    <section id="shift-rates" class="rates-section home-section">
      <div class="container rates-teaser">
        <div>
          <p class="eyebrow">03 / {{ $t("Shift Rates") }}</p>
          <h2>{{ $t("premium.ratesTitle") }}</h2>
          <p class="rates-teaser-copy">{{ $t("ratesPage.preview") }}</p>
        </div>
        <router-link class="btn btn-primary" to="/shift-rates">{{ $t("ratesPage.viewRates") }} <i class="fa fa-arrow-right ms-2" aria-hidden="true"></i></router-link>
      </div>
    </section>
    <public-faq />
    <contact-us ref="contact" />
  </main>
</template>

<script>
import { homeGraph, publicHead, serializeGraph } from "@/helpers/publicSeo"
import StatsComponent from "@/components/StatsComponent.vue"
import HowItWorks from "@/components/HowItWorks.vue"
import PublicFaq from "@/components/PublicFaq.vue"
import ContactUs from "@/views/ContactUs"
import { getAnalytics, logEvent } from "firebase/analytics"

export default {
  name: "HomeView",
  components: { ContactUs, HowItWorks, PublicFaq, StatsComponent },
  head() {
    const base = process.env.VUE_APP_SITE_URL
    const locale = this.$i18n.locale
    return {
      ...publicHead("home", base),
      script: [{
        key: "home-structured-data",
        type: "application/ld+json",
        textContent: serializeGraph(homeGraph(key => this.$t(key), base, locale === "gr" ? "el" : locale))
      }]
    }
  },
  mounted() {
    this.setupScrollReveals()
    logEvent(getAnalytics(), "page_view", { page_title: this.$route.name })
  },
  beforeUnmount() {
    this.revealObserver?.disconnect()
  },
  methods: {
    setupScrollReveals() {
      if (!window.IntersectionObserver || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        return
      }

      // Content stays visible without animation support or before observation.
      this.revealObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("care-revealed")
            this.revealObserver.unobserve(entry.target)
          }
        })
      }, { threshold: 0.08 })

      this.$el.querySelectorAll(
        ".about-intro, .audience-grid, .rates-teaser, .benefits-section .box, .contact-story, .section-contact-us .card"
      ).forEach((element) => this.revealObserver.observe(element))
    }
  }
}
</script>

<style scoped>
.rates-teaser { display: flex; align-items: center; justify-content: space-between; gap: 36px; text-align: start; }
.rates-teaser-copy { max-width: 650px; margin: 20px 0 0; color: var(--care-muted); line-height: 1.8; }
.rates-teaser .btn { flex-shrink: 0; }
@media (max-width: 767px) { .rates-teaser { align-items: flex-start; flex-direction: column; gap: 24px; } }

.hero-stage .hero-photo {
  object-position: center 30%;
}
.about-photograph > img {
  object-position: center 35%;
}
</style>
